<script lang="ts" setup>
import { isTouchScreen } from '@@/bits/browser'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { availableRealLocales, isRtlLocale } from '@@/bits/locale'
import { getSearchParam } from '@@/bits/location'
import GlobeIconWithIndicator from '@@/library/v4/components/GlobeIconWithIndicator.vue'
import OzCloseButton from '@@/library/v4/components/OzCloseButton.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzDrawerStatic from '@@/library/v4/components/OzDrawerStatic.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, { OzMenuRowsFocusStyle, OzMenuRowsSizePreset } from '@@/library/v4/components/OzMenuRows.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import {
  ANOTHER_LANGUAGE_KEY,
  ORIGINAL_LANGUAGE_KEY,
  useSurfaceTranslationsStore,
} from '@@/pinia/surface_translations_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'

interface TranslationBannerConfig {
  actionText?: string
  body?: string
  action?: () => Promise<void>
}

withDefaults(
  defineProps<{
    buttonClasses?: Record<string, boolean>
    isDarkMode: boolean
  }>(),
  {
    buttonClasses: undefined,
  },
)

const windowSizeStore = useWindowSizeStore()
const { isSmallerThanTabletPortrait, isPhone } = storeToRefs(windowSizeStore)

const surfaceStore = useSurfaceStore()
const { buttonDarkMode } = storeToRefs(surfaceStore)

const surfaceAccountsStore = useSurfaceAccountsStore()
const { currentUser } = storeToRefs(surfaceAccountsStore)

const surfaceTranslationsStore = useSurfaceTranslationsStore()
const {
  isTranslatingPadletFromBanner,
  userLocaleDisplayName,
  xRevertToOriginalBanner,
  xTranslateToUserLocaleBanner,
  userLocale,
  currentLanguageDisplayName,
  detectedLanguageDisplayName,
  isMultipleLanguagesDetected,
  hasDismissedTranslationBanner,
  isBannerExpanded,
  isViewingTranslatedPadlet,
  detectedLanguage,
  currentLanguage,
} = storeToRefs(surfaceTranslationsStore)

const globeIconSize = computed(() => (isSmallerThanTabletPortrait.value ? 24 : 20))

// Default menu row data on the mobile drawer
const defaultRowData = computed<OzMenuRowsRow[]>(() => {
  const rows: OzMenuRowsRow[] = [surfaceTranslationsStore.createOriginalLanguageRow(currentLanguage.value)]

  // Add user locale if it's different from detected language
  const userLocaleInfo = availableRealLocales.find((locale) => locale.html === userLocale.value)
  if (userLocaleInfo && userLocaleInfo.html !== detectedLanguage.value) {
    rows.push(surfaceTranslationsStore.createLocaleRow(userLocaleInfo, currentLanguage.value))
  }

  // Add current language if it's not original language or user locale
  if (
    !surfaceTranslationsStore.isOriginalLanguage(currentLanguage.value) &&
    currentLanguage.value !== userLocale.value
  ) {
    const currentLanguageInfo = availableRealLocales.find((locale) => locale.html === currentLanguage.value)
    if (currentLanguageInfo) {
      rows.push(surfaceTranslationsStore.createLocaleRow(currentLanguageInfo, currentLanguage.value))
    }
  }

  return [...rows, surfaceTranslationsStore.createAnotherLanguageRow()]
})

// Menu row data on the mobile drawer when the user clicks on "Another language"
const allLanguagesRowData = computed<OzMenuRowsRow[]>(() => {
  // Filter out the detected language and create menu rows for all locales
  const filteredLocales = availableRealLocales.filter((locale) => locale.html !== detectedLanguage.value)
  const localeRows = filteredLocales.map((locale) =>
    surfaceTranslationsStore.createLocaleRow(locale, currentLanguage.value),
  )

  const priorityRows: OzMenuRowsRow[] = []

  // Find and possibly move user locale to priority
  const userLocaleIndex = localeRows.findIndex((row) => row.ref === userLocale.value)
  if (userLocaleIndex !== -1 && userLocale.value !== detectedLanguage.value) {
    priorityRows.push(localeRows.splice(userLocaleIndex, 1)[0])
  }

  // Find and possibly move English to priority. Find 'en' first
  // and then check if it is not the detected language or user locale
  const englishIndex = localeRows.findIndex((row) => row.ref === 'en')
  if (englishIndex !== -1 && detectedLanguage.value !== 'en' && userLocale.value !== 'en') {
    priorityRows.push(localeRows.splice(englishIndex, 1)[0])
  }

  return [surfaceTranslationsStore.createOriginalLanguageRow(currentLanguage.value), ...priorityRows, ...localeRows]
})

const showMoreLanguages = ref(false)
const handleLanguageRowClick = async (eventPayload: string): Promise<void> => {
  if (eventPayload === ANOTHER_LANGUAGE_KEY) {
    showMoreLanguages.value = true
    return
  }

  // Close drawer first so that the user can see the translation in progress
  handleCloseDrawer()
  await surfaceTranslationsStore.translatePadletFromBanner(eventPayload)
}

const ignoreNextMouseEnter = ref(false)
const isTouchDevice = isTouchScreen()

// We need to have this guard to prevent the focus out event from being triggered when the focus is shifting
const isFocusShifting = ref(false)
const actionButtonRef = ref<InstanceType<typeof OzContainedButton> | null>(null)
const actionButtonEl = computed(() => actionButtonRef.value?.$el as HTMLElement)

const isDrawerExpanded = ref(false)

const handleHideButtonClick = (): void => {
  // We need to ignore the next mouse enter event because the hide button is inside the banner
  // and the mouse enter event will trigger the banner to stay open when the user clicks the hide button
  // since the mouse is still over the banner
  ignoreNextMouseEnter.value = true
  surfaceTranslationsStore.collapseBanner()
}

// Core banner expansion logic (shared between mouse and focus handlers)
const expandBanner = (): void => {
  clearCollapseTimeout()
  surfaceTranslationsStore.expandBanner()
}

// Core banner collapse logic (shared between mouse and focus handlers)
const collapseBanner = (): void => {
  startCollapseTimeout()
  // We don't want the banner to close before the translation is done
  if (hasDismissedTranslationBanner.value && !isTranslatingPadletFromBanner.value) {
    surfaceTranslationsStore.collapseBanner()
  }
}

const handleMouseEnter = (): void => {
  // Ignore mouse events during touch interactions
  if (isTouchDevice) return

  if (ignoreNextMouseEnter.value) {
    ignoreNextMouseEnter.value = false
    return
  }
  expandBanner()
}

const handleMouseLeave = (): void => {
  // Ignore mouse events during touch interactions
  if (isTouchDevice) return

  collapseBanner()
}

const handleFocusIn = async (): Promise<void> => {
  const wasCollapsed = !isBannerExpanded.value
  expandBanner()

  if (isTouchDevice) {
    // We need to do this so the hide button shows when the user clicks the globe icon on touch devices
    surfaceTranslationsStore.unDismissTranslationBanner()
  }

  if (wasCollapsed) {
    isFocusShifting.value = true

    // We need to wait for the next tick to ensure the action button is mounted so we can focus on it
    await nextTick()
    actionButtonEl.value?.focus()
    isFocusShifting.value = false
  }
}

const handleFocusOut = (event: FocusEvent): void => {
  if (isFocusShifting.value) {
    return
  }

  // If the related target is inside the container, we don't want to handle the focus out event
  const container = event.currentTarget as Node
  const relatedTarget = event.relatedTarget as Node | null

  if (relatedTarget && container.contains(relatedTarget)) {
    return
  }
  collapseBanner()
}

const handleCloseDrawer = (): void => {
  isDrawerExpanded.value = false
  showMoreLanguages.value = false
}

const translationBannerConfig = computed<TranslationBannerConfig>(() => {
  if (xRevertToOriginalBanner.value) {
    return {
      body: __('Viewing padlet in %{currentLanguage}', {
        currentLanguage: currentLanguageDisplayName.value,
      }),
      actionText: __('Revert to original'),
      action: async () => {
        await surfaceTranslationsStore.translatePadletFromBanner(ORIGINAL_LANGUAGE_KEY)
      },
    }
  } else if (xTranslateToUserLocaleBanner.value) {
    let body = ''
    if (isAppUsing('translatePadletV2')) {
      body = isMultipleLanguagesDetected.value
        ? __('Viewing padlet in multiple languages (Detected)')
        : __('Viewing padlet in %{detectedLanguage} (Detected)', {
            detectedLanguage: detectedLanguageDisplayName.value,
          })
    } else {
      body = isMultipleLanguagesDetected.value
        ? __('Viewing padlet in original languages')
        : __('Viewing padlet in original language (%{detectedLanguage})', {
            detectedLanguage: detectedLanguageDisplayName.value,
          })
    }

    return {
      body,
      actionText: __('Translate to %{userLocale}', {
        userLocale: userLocaleDisplayName.value,
      }),
      action: async () => {
        await surfaceTranslationsStore.translatePadletFromBanner(userLocale.value)
      },
    }
  } else {
    return {}
  }
})

const bannerDirAttribute = computed<'ltr' | 'rtl'>(() => {
  const manuallySetLocale = getSearchParam('locale')
  return isRtlLocale(manuallySetLocale ?? currentUser.value.lang) ? 'rtl' : 'ltr'
})

const collapseTimeout = ref<number | undefined>()

const startCollapseTimeout = () => {
  if (!isAppUsing('translatePadletV2')) {
    return
  }

  clearCollapseTimeout()
  collapseTimeout.value = window.setTimeout(() => {
    if (isBannerExpanded.value) {
      surfaceTranslationsStore.collapseBanner()
    }
  }, 8000)
}

const clearCollapseTimeout = () => {
  if (collapseTimeout.value) {
    clearTimeout(collapseTimeout.value)
    collapseTimeout.value = undefined
  }
}

onMounted(() => {
  startCollapseTimeout()
})

onUnmounted(() => {
  clearCollapseTimeout()
})
</script>

<template>
  <Transition v-if="isAppUsing('translatePadletV2') && !isSmallerThanTabletPortrait" name="slide">
    <div
      :class="[
        'flex items-center rounded-xl transition-all duration-300 ease-in-out text-body-extra-small',
        isDarkMode ? 'bg-button-dark' : 'bg-button-light',
        isBannerExpanded ? 'gap-2 ps-2 pe-1 py-0.5 h-7' : 'h-8 w-8 justify-center',
      ]"
      :dir="bannerDirAttribute"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
    >
      <div v-if="isBannerExpanded" class="flex items-center gap-2">
        <GlobeIconWithIndicator
          :dark-mode="isDarkMode"
          :size="globeIconSize"
          :show-indicator="isViewingTranslatedPadlet"
        />
        <p :class="['font-sans', isDarkMode ? 'text-light-text-200' : 'text-dark-text-200']">
          {{ translationBannerConfig.body }}
        </p>
        <OzContainedButton
          ref="actionButtonRef"
          :class="['backdrop-blur-sm', buttonClasses]"
          :color-scheme="OzContainedButtonColorScheme.Bare"
          :size-preset="OzContainedButtonSizePreset.H24px"
          :dark-mode="buttonDarkMode"
          :disabled="isTranslatingPadletFromBanner"
          data-testid="surfaceTranslationBannerActionButton"
          @click="translationBannerConfig.action"
        >
          <span :class="[isTranslatingPadletFromBanner && 'invisible']">
            {{ translationBannerConfig.actionText }}
          </span>
          <OzIndeterminateSpinner
            v-if="isTranslatingPadletFromBanner"
            :size="16"
            class="!absolute"
            :dark-mode="buttonDarkMode"
          />
        </OzContainedButton>
        <OzContainedButton
          v-if="!hasDismissedTranslationBanner"
          class="px-3"
          :text="__('Hide')"
          :size-preset="OzContainedButtonSizePreset.H24px"
          :dark-mode="buttonDarkMode"
          :disabled="isTranslatingPadletFromBanner"
          :color-scheme="OzContainedButtonColorScheme.SecondaryClear"
          @click="handleHideButtonClick"
        />
      </div>
      <OzPlainButton
        v-else
        :size-preset="OzPlainButtonSizePreset.H24px"
        :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
        :dark-mode="buttonDarkMode"
      >
        <template #icon>
          <GlobeIconWithIndicator
            :dark-mode="isDarkMode"
            :size="globeIconSize"
            :show-indicator="isViewingTranslatedPadlet"
          />
        </template>
      </OzPlainButton>
    </div>
  </Transition>

  <div v-else-if="isAppUsing('translatePadletV2') && isSmallerThanTabletPortrait">
    <OzContainedButton
      :size-preset="OzContainedButtonSizePreset.H40px"
      :color-scheme="OzContainedButtonColorScheme.SecondaryClear"
      :dark-mode="buttonDarkMode"
      @click="isDrawerExpanded = true"
    >
      <template #icon>
        <GlobeIconWithIndicator
          :dark-mode="isDarkMode"
          :size="globeIconSize"
          :show-indicator="isViewingTranslatedPadlet"
          :is-loading="isTranslatingPadletFromBanner"
        />
      </template>
    </OzContainedButton>

    <OzDrawerStatic
      v-if="isDrawerExpanded"
      :dark-mode="isDarkMode"
      :is-phone="isPhone"
      :width-class="'w-100'"
      :full-size="showMoreLanguages"
      :top-inner-corner-radius="0"
      @scrim-click="handleCloseDrawer"
      @scrim-esc="handleCloseDrawer"
    >
      <template #header>
        <div class="flex items-center px-4 pt-4 pb-3">
          <OzIconButton
            icon-name="close_small"
            :size-preset="OzIconButtonSizePreset.H32px"
            :label="__('Close language panel')"
            :color-scheme="OzIconButtonColorScheme.Secondary"
            :dark-mode="buttonDarkMode"
            data-testid="surfaceTranslationBannerCloseButton"
            @click="handleCloseDrawer"
          />
          <div class="grow ps-4 pe-12">
            <h2 class="text-heading-3 font-semibold text-center truncate">
              {{ __('Language') }}
            </h2>
          </div>
        </div>
      </template>
      <template #body>
        <div
          class="overflow-y-scroll scrollbar-regular dark:scrollbar-regular-dark px-2 pt-3 pb-6"
          data-testid="surfaceTranslationBannerLanguageMenu"
        >
          <OzMenuRows
            :accessibility-role="'menu'"
            :aria-label="__('Language')"
            :dark-mode="isDarkMode"
            :is-phone="isPhone"
            :active-row="currentLanguage"
            :focus-style="OzMenuRowsFocusStyle.Bubble100"
            :size-preset="OzMenuRowsSizePreset.H48px"
            :rows="showMoreLanguages ? allLanguagesRowData : defaultRowData"
            @select-row="(_event, eventPayload) => handleLanguageRowClick(eventPayload)"
          />
        </div>
      </template>
    </OzDrawerStatic>
  </div>

  <Transition
    v-else
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div class="flex items-center gap-2" :dir="bannerDirAttribute">
      <OzCloseButton
        :size-preset="OzContainedButtonSizePreset.H24px"
        :dark-mode="buttonDarkMode"
        :close-icon-size="'small'"
        :disabled="isTranslatingPadletFromBanner"
        :color-scheme="OzIconButtonColorScheme.SecondaryClear"
        @click="surfaceTranslationsStore.dismissTranslationBanner"
      />
      <p :class="['font-sans', isDarkMode ? 'text-light-text-100' : 'text-dark-text-100']">
        {{ translationBannerConfig.body }}
      </p>
      <OzContainedButton
        :class="['backdrop-blur-sm', buttonClasses]"
        :color-scheme="OzContainedButtonColorScheme.Bare"
        :size-preset="OzContainedButtonSizePreset.H24px"
        :dark-mode="buttonDarkMode"
        :disabled="isTranslatingPadletFromBanner"
        data-testid="surfaceTranslationBannerActionButton"
        @click="translationBannerConfig.action"
      >
        <span :class="[isTranslatingPadletFromBanner && 'invisible']">
          {{ translationBannerConfig.actionText }}
        </span>
        <OzIndeterminateSpinner
          v-if="isTranslatingPadletFromBanner"
          :size="16"
          class="!absolute"
          :dark-mode="buttonDarkMode"
        />
      </OzContainedButton>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.slide-enter,
.slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
.slide-enter-to,
.slide-leave {
  transform: translateY(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .slide-enter,
  .slide-leave-to {
    transform: none;
  }
  .slide-enter-to,
  .slide-leave {
    transform: none;
  }
}

.slide-enter-active {
  transition: transform 0.25s cubic-bezier(0, 0, 0.2, 1), opacity 0.25s cubic-bezier(0, 0, 0.2, 1);
}
.slide-leave-active {
  transition: transform 0.18s cubic-bezier(0.4, 0, 1, 1), opacity 0.12s cubic-bezier(0.4, 0, 1, 1);
}
</style>
